import { useCallback, useEffect, useRef, useState } from 'react';
import { pageTitle } from '@/lib/page-title';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarClock, Coins, Loader2, Zap } from 'lucide-react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { StudioPassCard } from '@/components/pricing/StudioPassCard';
import { SystemChecks } from '@/components/trust/SystemChecks';
import { SupportLine } from '@/components/trust/SupportLine';
import { api } from '@/lib/api';
import { trackEvent, trackPageView } from '@/lib/analytics';
import {
  loadPassMember, loadPassOffer, mxn, setPassCancel, startPassCheckout,
  type PassMember, type PassOffer,
} from '@/lib/studio-pass';

// /pass: THE STUDIO PASS (blueprint 04, "monthly credits plus a priority
// queue"). The offer, or for a member their pass: when it renews, what each
// month brings, and one button to stop or keep renewing. Off until the owner
// sets STUDIO_PASS=1; until then the page says it is coming and points at
// the one-time packs.
//
// A signed-out visitor who taps the pay button signs up first and comes back
// to /pass?join=1, where checkout starts on its own, once.

const DATE_LOCALE: Record<string, string> = { es: 'es-MX', en: 'en-US' };

export default function StudioPass() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [offer, setOffer] = useState<PassOffer | null>(null);
  const [member, setMember] = useState<PassMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const checkout = params.get('checkout');
  const lang = String(i18n.language || 'en').slice(0, 2);
  const joinStarted = useRef(false);

  useEffect(() => {
    trackPageView('studio_pass', { checkout: checkout || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const previous = document.title;
    document.title = pageTitle(`${t('pass.metaTitle')} | FaceShot-ChopShop`);
    return () => {
      document.title = previous;
    };
  }, [t, i18n.language]);

  const refresh = useCallback(async () => {
    const [o, m] = await Promise.all([loadPassOffer(), loadPassMember()]);
    setOffer(o);
    setMember(m);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Back from Stripe: the webhook records the pass a moment after the
  // redirect, so look again a few times rather than show "no pass".
  useEffect(() => {
    if (checkout !== 'success') return undefined;
    let tries = 0;
    const timer = window.setInterval(async () => {
      tries += 1;
      const m = await loadPassMember();
      if (m) setMember(m);
      if ((m && m.active) || tries >= 6) window.clearInterval(timer);
    }, 2500);
    return () => window.clearInterval(timer);
  }, [checkout]);

  const join = useCallback(async () => {
    setError('');
    if (!api.getToken()) {
      trackEvent('studio_pass_signup_first', {});
      navigate(`/signup?redirect_to=${encodeURIComponent('/pass?join=1')}`);
      return;
    }
    setBusy(true);
    trackEvent('studio_pass_checkout_started', {});
    try {
      window.location.href = await startPassCheckout(lang);
    } catch (err) {
      const code = err instanceof Error ? err.message : 'checkout_failed';
      setError(t(code === 'already_member' ? 'pass.errors.already' : 'pass.errors.checkout'));
      if (code === 'already_member') void refresh();
      setBusy(false);
    }
  }, [lang, navigate, refresh, t]);

  // Back from sign-up with ?join=1: start checkout once.
  useEffect(() => {
    if (params.get('join') !== '1' || loading || joinStarted.current) return;
    joinStarted.current = true;
    const next = new URLSearchParams(params);
    next.delete('join');
    setParams(next, { replace: true });
    if (offer?.enabled && !member?.active && api.getToken()) void join();
  }, [params, setParams, loading, offer, member, join]);

  const toggleCancel = async (cancel: boolean) => {
    setError('');
    setBusy(true);
    try {
      setMember(await setPassCancel(cancel));
      trackEvent(cancel ? 'studio_pass_cancelled' : 'studio_pass_resumed', {});
    } catch {
      setError(t('pass.errors.update'));
    } finally {
      setBusy(false);
    }
  };

  const renews = member?.renews_at
    ? new Date(member.renews_at).toLocaleDateString(DATE_LOCALE[lang] || 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <div className="min-h-screen overflow-x-clip bg-background">
      <Navbar />
      <main className="pt-20 sm:pt-24">
        <div className="container mx-auto max-w-6xl px-4 md:px-8 xl:px-4">
          <div className="grid items-start gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-12">
            <header className="max-w-2xl lg:pt-6">
              <p className="eyebrow">{t('pass.name')}</p>
              <h1 className="mt-3 text-balance font-display text-[32px] font-bold leading-[1.06] tracking-[-0.02em] sm:text-[46px]">
                {t('pass.h1a')} <span className="gradient-text">{t('pass.h1b')}</span>
              </h1>
              <p className="mt-3 text-pretty text-[15px] leading-relaxed text-muted-foreground sm:text-[1.05rem]">{t('pass.lead')}</p>

              <ol className="mt-6 space-y-3" aria-label={t('pass.how.title')}>
                {(['join', 'monthly', 'cancel'] as const).map((step, i) => (
                  <li key={step} className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/[0.05] font-mono text-[13px] font-semibold text-foreground">
                      {i + 1}
                    </span>
                    <span className="text-[15px] leading-snug">
                      <span className="font-semibold text-foreground">{t(`pass.how.${step}Title`)}</span>{' '}
                      <span className="text-muted-foreground">{t(`pass.how.${step}Body`)}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </header>

            <div className="w-full">
              {checkout === 'success' && (
                <p role="status" className="mb-4 rounded-2xl border border-accent/40 bg-accent/10 p-4 text-[15px]" data-testid="pass-welcome">
                  {member?.active ? t('pass.status.welcome') : t('pass.status.starting')}
                </p>
              )}
              {checkout === 'cancelled' && !member?.active && (
                <p role="status" className="mb-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-[15px] text-muted-foreground">
                  {t('pass.status.checkoutCancelled')}
                </p>
              )}

              {loading ? (
                <div className="flex min-h-[420px] items-center justify-center rounded-[1.35rem] border border-white/10 bg-white/[0.03]">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label={t('pass.loading')} />
                </div>
              ) : member?.active ? (
                <section
                  data-testid="pass-member"
                  className="hero-hairline rounded-[1.35rem] border border-primary/45 bg-[linear-gradient(180deg,rgba(244,190,82,0.10),rgba(255,255,255,0.02)_45%)] p-5 sm:p-7"
                >
                  <p className="flex items-center gap-1.5 font-mono text-[12px] font-semibold uppercase tracking-[0.18em] text-primary">
                    <Zap className="h-4 w-4" aria-hidden />
                    {t('pass.member.eyebrow')}
                  </p>
                  <h2 className="mt-1 font-display text-[26px] font-bold leading-tight sm:text-[30px]">{t('pass.member.title')}</h2>
                  <dl className="mt-5 space-y-3 text-[15px]">
                    <div className="flex items-start gap-3">
                      <Coins className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
                      <div>
                        <dt className="text-muted-foreground">{t('pass.member.eachMonth')}</dt>
                        <dd className="font-semibold tabular-nums">{t('pass.card.creditsChip', { credits: member.credits.toLocaleString('en-US') })} · {mxn(member.price_cents)} MXN</dd>
                      </div>
                    </div>
                    {renews && (
                      <div className="flex items-start gap-3">
                        <CalendarClock className="mt-0.5 h-5 w-5 shrink-0 text-accent" aria-hidden />
                        <div>
                          <dt className="text-muted-foreground">{member.cancel_at_period_end ? t('pass.member.endsOn') : t('pass.member.renewsOn')}</dt>
                          <dd className="font-semibold" data-testid="pass-renews">{renews}</dd>
                        </div>
                      </div>
                    )}
                  </dl>
                  {offer?.priority && <p className="mt-4 text-[14px] text-muted-foreground">{t('pass.member.priority')}</p>}
                  <p className="mt-2 text-[14px] text-muted-foreground">
                    {member.cancel_at_period_end ? t('pass.member.cancelledNote') : t('pass.member.keepNote')}
                  </p>
                  <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                    <Link
                      to="/#credits"
                      className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-full border border-white/15 bg-white/[0.06] px-5 font-semibold hover:border-primary/50 hover:bg-primary/10"
                    >
                      {t('pass.member.useCredits')}
                    </Link>
                    <button
                      type="button"
                      data-testid={member.cancel_at_period_end ? 'pass-resume' : 'pass-cancel'}
                      disabled={busy}
                      onClick={() => toggleCancel(!member.cancel_at_period_end)}
                      className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full border border-white/15 bg-transparent px-5 font-semibold text-foreground hover:border-white/30 disabled:opacity-60"
                    >
                      {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                      {member.cancel_at_period_end ? t('pass.member.resume') : t('pass.member.cancel')}
                    </button>
                  </div>
                </section>
              ) : offer ? (
                <>
                  <StudioPassCard offer={offer} onJoin={join} busy={busy} showWhenOff />
                  {!offer.enabled && (
                    <p className="mt-4 text-center text-[15px] text-muted-foreground">
                      {t('pass.offNote')}{' '}
                      <Link to="/#credits" className="font-semibold text-primary underline-offset-4 hover:underline">{t('pass.offLink')}</Link>
                    </p>
                  )}
                </>
              ) : (
                <p className="rounded-2xl border border-white/10 p-4 text-[15px] text-muted-foreground">{t('pass.errors.load')}</p>
              )}
              {error && <p role="alert" className="mt-4 text-center text-[15px] text-destructive">{error}</p>}
            </div>
          </div>

          <div className="mt-14 space-y-12 pb-16">
            <SystemChecks checks={['cardsToStripe', 'failedRefunds']} />
            <SupportLine where="studio_pass" />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
